"use client";

import React, { useEffect, useRef, useState } from "react";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import inputStyles from "@/app/(components)/generics/inputs/default/Input/Input.module.css";
import defaultInputStyles from "@/app/(components)/generics/inputs/styles/DefaultInput.module.css";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import {
  COUNTRY_CODES,
  CountryCode,
  DEFAULT_COUNTRY_CODE,
  getFlagUrl,
} from "@/app/vars/CountryCodes";
import styles from "./PhoneInput.module.css";

type Props = {
  lang?: string;
  data: any;
  dataKey: string;
  label?: string;
  labelSuffix?: React.ReactNode;
  required?: boolean;
  showRequiredLabel?: boolean;
  placeholder?: string;
  errorMessage?: string;
  disabled?: boolean;
  onChange?: (data: any) => void;
};

function parseInitialValue(value: string): { country: CountryCode; number: string } {
  if (value) {
    const match = COUNTRY_CODES.find(
      (c) => value.startsWith(`${c.dial} `) || value.startsWith(c.dial),
    );
    if (match) {
      return { country: match, number: value.slice(match.dial.length).trimStart() };
    }
  }
  return { country: DEFAULT_COUNTRY_CODE, number: value ?? "" };
}

export default function PhoneInput(props: Props) {
  const {
    lang,
    data,
    dataKey,
    label,
    labelSuffix,
    required,
    showRequiredLabel,
    placeholder,
    errorMessage,
    disabled,
    onChange,
  } = props;

  const initial = parseInitialValue(data[dataKey] ?? "");
  const [country, setCountry] = useState<CountryCode>(initial.country);
  const [number, setNumber] = useState<string>(initial.number);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const emit = (nextCountry: CountryCode, nextNumber: string) => {
    const combined = nextNumber ? `${nextCountry.dial} ${nextNumber}` : "";
    data[dataKey] = combined;
    onChange?.(data);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className={styles.container}>
      {label && (
        <label className={inputStyles.label}>
          {label}
          {showRequiredLabel &&
            (required ? (
              <span className={inputStyles.labelRequired}>*</span>
            ) : (
              <span className={inputStyles.labelOptional}>({T(TKey.OPTIONAL, lang)})</span>
            ))}
          {labelSuffix && <span className={inputStyles.labelSuffix}>{labelSuffix}</span>}
        </label>
      )}

      <div className={`${styles.inputRow} ${disabled ? styles.disabled : ""}`}>
        <div className={styles.dropdownWrapper} ref={dropdownRef}>
          <button
            type="button"
            className={styles.dialButton}
            onClick={() => !disabled && setOpen((v) => !v)}
            disabled={disabled}
          >
            <img src={getFlagUrl(country.code)} alt={country.code} className={styles.flag} />
            <span className={styles.dial}>{country.dial}</span>
            <span className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}>
              <IconArrowDown size={14} />
            </span>
          </button>

          {open && (
            <div className={styles.dropdown}>
              {COUNTRY_CODES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  className={`${styles.dropdownItem} ${c.code === country.code ? styles.selected : ""}`}
                  onClick={() => {
                    setCountry(c);
                    setOpen(false);
                    emit(c, number);
                  }}
                >
                  <img src={getFlagUrl(c.code)} alt={c.code} className={styles.itemFlag} />
                  <span className={styles.itemDial}>{c.dial}</span>
                  <span className={styles.itemCode}>{c.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          type="tel"
          className={styles.numberInput}
          placeholder={placeholder}
          value={number}
          disabled={disabled}
          onChange={(e) => {
            const val = e.target.value;
            if (val.startsWith("+")) {
              // Sort longest dial first to avoid "+1" matching "+12X" patterns
              const match = [...COUNTRY_CODES]
                .sort((a, b) => b.dial.length - a.dial.length)
                .find((c) => val.startsWith(`${c.dial} `) || val.startsWith(c.dial));
              if (match) {
                const rest = val.slice(match.dial.length).trimStart();
                setCountry(match);
                setNumber(rest);
                emit(match, rest);
                return;
              }
            }
            setNumber(val);
            emit(country, val);
          }}
        />
      </div>

      {errorMessage && (
        <div className={defaultInputStyles.errorMessage}>
          <ErrorMessage message={errorMessage} />
        </div>
      )}
    </div>
  );
}
